// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { formatAmount } from '../../lib/eventLog.js';
import { formatDate } from '../../lib/formatLocale.js';

export function TransactionReport() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;

  const now = new Date();
  const [dateFrom, setDateFrom] = useState(`${now.getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(now.toISOString().slice(0, 10));
  const [accountFilter, setAccountFilter] = useState('');

  const accounts = [...ledger.accounts.values()].sort((a, b) => a.name.localeCompare(b.name));

  const rows = useMemo(() => {
    const out: {
      date: string;
      num: string;
      description: string;
      accountName: string;
      transferName: string;
      amount: number;
      currency: string;
    }[] = [];

    for (const tx of ledger.transactions.values()) {
      if (tx.voided) continue;
      if (tx.date < dateFrom || tx.date > dateTo) continue;

      for (const split of tx.splits) {
        if (accountFilter && split.accountId !== accountFilter) continue;

        const account = ledger.accounts.get(split.accountId);
        if (!account) continue;

        const otherSplits = tx.splits.filter((s) => s.accountId !== split.accountId);
        const firstOther = otherSplits[0];
        const transferName: string =
          otherSplits.length === 1 && firstOther !== undefined
            ? ledger.accounts.get(firstOther.accountId)?.name ?? String(t('reports:transactionReport.splitFallback'))
            : String(t('reports:transactionReport.split'));

        out.push({
          date: tx.date,
          num: tx.num,
          description: tx.description,
          accountName: account.name,
          transferName,
          amount: split.amount,
          currency: tx.currency,
        });
      }
    }

    out.sort((a, b) => a.date.localeCompare(b.date) || a.description.localeCompare(b.description));
    return out;
  }, [ledger.transactions, ledger.accounts, dateFrom, dateTo, accountFilter, t]);

  const total = rows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <h2 className="text-md font-semibold text-text">{t('reports:titles.transactionReport')}</h2>
        <div className="flex items-center gap-3 ml-auto flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.from')}</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-text-muted">{t('reports:controls.to')}</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none" />
          </div>
          <select value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}
            className="bg-bg border border-border text-text text-sm px-2 py-1 focus:border-accent focus:outline-none">
            <option value="">{t('reports:controls.allAccounts')}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={() => window.print()}
            className="px-3 py-1 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors no-print">
            {t('reports:controls.print')}
          </button>
        </div>
      </div>

      <div className="bg-bg-surface border border-border print:bg-white print:border-gray-200 print-full">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-bg-elevated border-b border-border-strong print:bg-gray-50 print:border-gray-300">
            <tr>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-3 print:text-gray-500">{t('reports:columns.date')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2 print:text-gray-500">{t('reports:columns.num')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2 print:text-gray-500">{t('reports:columns.description')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2 print:text-gray-500">{t('reports:columns.account')}</th>
              <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-2 print:text-gray-500">{t('reports:columns.transfer')}</th>
              <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-3 print:text-gray-500">{t('reports:columns.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-text-subtle text-sm print:text-gray-400">{t('reports:empty.noTransactionsFound')}</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-border hover:bg-bg-elevated print:border-gray-100 print:hover:bg-transparent">
                <td className="py-1.5 px-3 text-text-muted font-mono text-xs whitespace-nowrap print:text-gray-600">{formatDate(r.date, { month: '2-digit', day: '2-digit', year: '2-digit' })}</td>
                <td className="py-1.5 px-2 font-mono text-xs text-text-subtle print:text-gray-500">{r.num}</td>
                <td className="py-1.5 px-2 text-text print:text-gray-900">{r.description}</td>
                <td className="py-1.5 px-2 text-text-muted text-xs print:text-gray-700">{r.accountName}</td>
                <td className="py-1.5 px-2 text-text-subtle text-xs print:text-gray-500">{r.transferName}</td>
                <td className={`py-1.5 px-3 text-right font-mono text-sm ${r.amount >= 0 ? 'text-positive print:text-green-700' : 'text-negative print:text-red-700'}`}>
                  {formatAmount(r.amount, r.currency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-border-strong bg-bg-elevated print:border-gray-300 print:bg-gray-50">
            <tr>
              <td colSpan={5} className="py-1.5 px-3 text-sm font-semibold text-text print:text-gray-700">{t('reports:totalRows', { count: rows.length })}</td>
              <td className={`py-1.5 px-3 text-right font-mono font-semibold text-sm ${total >= 0 ? 'text-positive print:text-green-700' : 'text-negative print:text-red-700'}`}>
                {formatAmount(total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
